/**
 * TEMPORARY compensation for crossed theme-variable bindings in the docpilot
 * design-library slides (staging design folder): full-bleed surfaces there
 * reference `--theme-color-1` (the Icons & elements slot) and tab colours
 * reference `--theme-color-3` (the Accent background slot), so the Ready
 * step's rows repaint each other's elements.
 *
 * This rewrites exactly these crossings on generated slides:
 *   - surface keys (`backgroundColor` / `overlayColor`) bound to
 *     `--theme-color-1` become `--theme-color-3`
 *   - `tabs*` colour keys bound to `--theme-color-3` become `--theme-color-1`
 *   - Icon entity `color`s bound to `--theme-color-2` (the pale tint slot)
 *     or `--theme-color-3` (the accent background slot) become
 *     `--theme-color-4` — the design library paints icons with any of
 *     -2/-3/-4 interchangeably, but only -1/-4 follow the funnel's
 *     "Icons & elements" pick (hex and background-var icons are deliberate
 *     contrast choices and stay untouched, except the placeholder gray below)
 *   - Icon entity `color`s left at the designer placeholder gray `#cdcdcd`
 *     (an unbound leftover, not a design choice) become `--theme-color-4`
 *   - inline `color:` declarations on `<li>` styles in rich-text strings are
 *     stripped — the design slides bind some bullets to `var(--title-color)`
 *     and leave siblings unbound, so lists render two colours; stripped, all
 *     bullets inherit the text block's own colour
 *
 * DELETE this once the design-folder slides are rebound — it will then be a
 * no-op for fixed designs but would still override any design that
 * deliberately paints a surface with the icons colour.
 */

const THEME_1 = "--theme-color-1";
const THEME_2 = "--theme-color-2";
const THEME_3 = "--theme-color-3";
const THEME_4 = "--theme-color-4";

const SURFACE_KEYS = new Set(["backgroundColor", "overlayColor"]);

// One rebinding table per rule, applied with a single swap helper.
const SURFACE_REBINDS = { [THEME_1]: THEME_3 };
const TABS_REBINDS = { [THEME_3]: THEME_1 };
const ICON_REBINDS = { [THEME_2]: THEME_4, [THEME_3]: THEME_4 };

// Identifier boundary so "--theme-color-1" can never rewrite a longer name
// like "--theme-color-1-muted" (none exist today; cheap insurance).
const swapVars = (value: string, rebinds: Record<string, string>) =>
  Object.entries(rebinds).reduce(
    (result, [from, to]) =>
      result.replace(new RegExp(`${from}(?![\\w-])`, "g"), to),
    value,
  );

// An Icon entity's settings carry the glyph name alongside its colour —
// the only place a `color` key sits next to a string `icon` key.
const isIconSettings = (node: Record<string, unknown>) =>
  typeof node.icon === "string" && typeof node.color === "string";

/** The design library's unbound-icon leftover; never a deliberate colour. */
const isPlaceholderGray = (value: string) => {
  const hex = value.trim().toLowerCase();
  return hex === "#cdcdcd" || hex === "#cdcdcdff";
};

// Drops bare `color:` declarations while leaving `background-color` (and any
// other `-color` property) intact. Split on `;` rather than matching around
// the property boundary: a lookbehind would be the obvious tool, but regex
// literals compile at module parse time, so `(?<!...)` throws a SyntaxError on
// Safari < 16.4 and takes the whole funnel chunk down with it.
const isBareColorDeclaration = (declaration: string) => {
  const property = declaration.split(":")[0];
  return property !== undefined && property.trim().toLowerCase() === "color";
};

const stripListItemColors = (html: string) =>
  html.replace(
    /(<li\b[^>]*style=")([^"]*)/g,
    (_, prefix: string, style: string) => {
      const kept = style
        .split(";")
        .filter((declaration) => !isBareColorDeclaration(declaration));
      // Re-terminate so `a:1;color:red;b:2` and `a:1;color:red` both stay
      // well-formed, and an all-stripped style collapses to nothing.
      const joined = kept.join(";").trim();
      return prefix + (joined === ";" ? "" : joined);
    },
  );

const normalizeObject = (node: Record<string, unknown>) => {
  const iconSettings = isIconSettings(node);
  for (const [key, value] of Object.entries(node)) {
    if (typeof value === "string") {
      if (SURFACE_KEYS.has(key)) {
        node[key] = swapVars(value, SURFACE_REBINDS);
      } else if (key.startsWith("tabs")) {
        node[key] = swapVars(value, TABS_REBINDS);
      } else if (iconSettings && key === "color") {
        node[key] = isPlaceholderGray(value)
          ? `var(${THEME_4})`
          : swapVars(value, ICON_REBINDS);
      }
      if (
        typeof node[key] === "string" &&
        (node[key] as string).includes("<li")
      ) {
        node[key] = stripListItemColors(node[key] as string);
      }
      continue;
    }
    if (value && typeof value === "object") {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item && typeof item === "object") {
            normalizeObject(item as Record<string, unknown>);
          }
        }
      } else {
        normalizeObject(value as Record<string, unknown>);
      }
    }
  }
};

/** Mutates and returns the slide JSON with the crossed bindings swapped. */
export const normalizeSlideThemeVars = <T>(slideJson: T): T => {
  if (slideJson && typeof slideJson === "object") {
    normalizeObject(slideJson as Record<string, unknown>);
  }
  return slideJson;
};
